import { combineLatest, Observable, of, catchError, filter, map, switchMap, take } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { OccEndpointsService, UserIdService } from '@spartacus/core';
import { ActiveCartService } from '@spartacus/cart/base/core';
import { Cart } from '@spartacus/cart/base/root';

import { IWarranty } from '@seb/web-design-system/core';

import { ExtendedWarrantyMapper } from '../mappers/extended-warranty.mapper';
import { WebCommonConfig } from '../config/configs/web-common.config';
import { ExtendedWarrantyConfig, CustomBaseStore } from '../models';
import { ConfigurationService } from './configuration.service';
import { BaseStoreService } from './base-store.service';

@Injectable({
  providedIn: 'root'
})
export class ExtendedWarrantyService {
  private get baseStore(): CustomBaseStore {
    return this.baseStoreService.baseStore;
  };

  constructor(
    private http: HttpClient,
    private occEndpointsService: OccEndpointsService,
    private baseStoreService: BaseStoreService,
    private configService: ConfigurationService,
    private userIdService: UserIdService,
    private activeCartService: ActiveCartService,
    private extendedWarrantyMapper: ExtendedWarrantyMapper
  ) {}

  getActiveCategories(extendedWarrantyConfig: ExtendedWarrantyConfig): Observable<string> {
    // Check if the condition to call the API is met
    const shouldCallApi = extendedWarrantyConfig?.enabled && extendedWarrantyConfig?.storeUID;
    if (localStorage.getItem('activeCategoriesExtendedWarranty') && this.checkExpiryDate(localStorage.getItem('activeCategoriesExtendedWarranty'))) {
      // Return cached data if available and not expired
      return of(localStorage.getItem('activeCategoriesExtendedWarranty'));
    } else if (shouldCallApi) {
      // Call the API if the condition is met
      return this.configService?.getConfig().pipe(
        switchMap((config: WebCommonConfig) => {
          const url = `${config.extendedWarranty.baseUrl}/consumer/ecommerce/v1/warranty/offers/active_categories?store_id=${extendedWarrantyConfig.storeUID}`;
          const headers = {
            'Ocp-Apim-Subscription-Key': config.extendedWarranty.subscriptionKey
          };
          return this.http.get<{ active_category_ids: [] }>(url, { headers }).pipe(map((activeCategories) => {
            const data = {
              value: activeCategories,
              expiry: Date.now() + Number(config.extendedWarranty.cache)
            };
            localStorage.setItem('activeCategoriesExtendedWarranty', JSON.stringify(data));
            return JSON.stringify(data);
          }));
        })
      );
    } else {
      // Return an observable with a default value if the condition is not met
      return of('{"value": {"active_category_ids": []}, "expiry": 0}');
    }
  }

  checkExpiryDate(data): boolean {
    const dataObj = JSON.parse(data);
    if (Date.now() > dataObj.expiry) {
      localStorage.removeItem('activeCategoriesExtendedWarranty');
      return false;
    } else {
      return true;
    }
  }

  checkProductEligibilityForExtendedWarranty(product): Observable<boolean> {
    return this.getActiveCategories(this.baseStore.extendedWarranty).pipe(
      map((activeCat) => {
        const activeCategories = JSON.parse(activeCat).value['active_category_ids'];

        // Check eligibility based on active categories
        const eligible = activeCategories.some((value) => (
          value === product?.family2 || value === product?.familyAnneeN
        ));

        // Check additional conditions for eligibility
        return this.baseStore.extendedWarranty?.enabled && !product.isAccessory && eligible;
      })
    );
  }

  getOffers(product) {
    return this.getActiveCategories(this.baseStore.extendedWarranty).pipe(
      switchMap((activeCat) => {
        const activeCategories = JSON.parse(activeCat).value['active_category_ids'];
        let activeCategoriesProduct;

        activeCategories.forEach((value) => {
          if (value === product?.family2) {
            activeCategoriesProduct = value;
          } else if (value === product?.familyAnneeN) {
            activeCategoriesProduct = value;
          }
        });

        if (activeCategoriesProduct) {
          const activeCat = activeCategoriesProduct;
          const { extendedWarranty } = this.configService.config;

          let productPrice = product.discountedPrice ? Number(product.discountedPrice?.value) * 100 : Number(product.price?.value) * 100;

          productPrice = Math.floor(productPrice);

          const baseUrl = `${extendedWarranty.baseUrl}/consumer/ecommerce/v1/warranty/offers/categories/${activeCat}`;
          const queryParams = `?product_price=${productPrice}&store_id=${this.baseStore.extendedWarranty?.storeUID}`;
          const url = `${baseUrl}${queryParams}`;
          const headers = {
            'Ocp-Apim-Subscription-Key': extendedWarranty.subscriptionKey
          };
          const itemsToDisplay = Number(extendedWarranty.itemsToDisplay);

          return this.http.get<any>(url, { headers }).pipe(map((result) => {
            const offers = result?.offers?.map((offer) => {
              return {
                ...offer,
                price: offer.price / 100,
                total_price: offer.total_price / 100
              };
            });
            const _result = {
              ...result,
              offers: offers.slice(0, itemsToDisplay),
              category: activeCat
            };
            return _result;
          }));
        } else {
          return of(null);
        }
      })
    );
  }

  getAndSetExtendedWarrantyOffersData(product, entryNumber, currencies) {
    const extendedWarrantyOffers$ = this.getOffers(product).pipe(catchError(() => of(null)));
    let extendedWarrantyOffersData = [];

    return extendedWarrantyOffers$.pipe(
      switchMap((extendedWarrantyOffers) => {
        const locale = extendedWarrantyOffers?.locale;
        const offers = extendedWarrantyOffers?.offers;
        const category = extendedWarrantyOffers?.category;
        extendedWarrantyOffersData = [];
        offers?.map((offer, index) => extendedWarrantyOffersData.push(
          this.extendedWarrantyMapper.toWarranty(
            offer,
            locale,
            index,
            currencies,
            category,
            entryNumber
          )
        ));
        return of(extendedWarrantyOffersData);
      })
    );
  }

  updateExtendedWarranty(warranty: IWarranty, type: string): Observable<Cart> {
    const userId$ = this.userIdService.takeUserId();
    const cartId$ = this.activeCartService.getActiveCartId();
    return combineLatest([userId$, cartId$]).pipe(
      filter(([userId, cartId]) => !!userId && !!cartId),
      take(1),
      switchMap(([userId, cartId]) => {
        const url = this.occEndpointsService.buildUrl('updateExtendedWarranty', {
          urlParams: { userId, cartId, entryNumber: warranty.entryNumber },
          queryParams: { fields: 'FULL' }
        });
        if (type === 'ADD') {
          const body = {
            offerId: warranty.id,
            name: warranty.nameField + ' ' + warranty.list[0],
            priceValue: warranty.priceValue,
            category: warranty.category
          };
          return this.http.post<Cart>(url, body);
        } else if (type === 'DELETE') {
          return this.http.delete<Cart>(url, {});
        } else {
          return of(null);
        }
      })
    );
  }
}
